import { css } from '@emotion/css';
import { TFunction } from 'i18next';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { RootState } from '../../../features';
import { actions, MyDesignDeleteErrorType } from '../../../features/myDesign';
import { MyDesign } from '../../../models/MyDesign';
import { ConfirmButton } from '../../shared/buttons/ConfirmButton';
import { NormalButton } from '../../shared/buttons/NormalButton';
import { reveal, shake } from '../../shared/keyframe-animations';

const detailPagePath = '/players/my-design/detail';

export const ConfirmDelete: React.FC = () => {
  const { t } = useTranslation(['my_design', 'translation']);
  const history = useHistory();
  const { deleteStatus, uploadedMyDesign, deleteErrorType } = useSelector(
    (state: RootState) => state.myDesign
  );
  const dispatch = useDispatch();
  const [snapshot, setSnapshot] = useState<MyDesign | undefined>(undefined);

  useEffect(() => {
    if (!snapshot) {
      setSnapshot(uploadedMyDesign);
      return;
    }
    if (deleteStatus === 'succeeded') {
      history.goBack();
    } else if (deleteStatus === 'failed' && deleteErrorType === 'no-exist') {
      history.replace(`/players/my-design/delete-failed?design=${snapshot.designType}`);
    }
  }, [deleteStatus, deleteErrorType, history, snapshot, uploadedMyDesign]);

  useEffect(() => {
    return () => {
      dispatch(actions.clearError());
    };
  }, [dispatch]);

  const handleConfirmClick = useCallback(() => {
    if (!deleteStatus || deleteStatus === 'failed') {
      dispatch(actions.deleteMyDesign());
    }
  }, [deleteStatus, dispatch]);

  const handleCancelClick = useCallback(() => {
    history.replace(`${detailPagePath}/menu/1`);
  }, [history]);

  const isLoading = deleteStatus === 'loading';
  const showError = deleteStatus === 'failed' && deleteErrorType !== 'no-exist';
  const error = getText(t, deleteErrorType);

  return (
    <div
      className={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        height: 100%;

        .title,
        .desc,
        .ok,
        .cancel {
          opacity: 0;
          visibility: hidden;
          transform: translateX(10px);
          animation: 0.2s ease-in-out ${reveal} both;
        }
        .title {
          font-size: 20px;
          text-align: center;
          color: #4d3906;
          overflow-wrap: break-word;
          animation-delay: 0.1s;
        }
        .desc {
          flex: 1 1 auto;
          padding: 12px 0;
          animation-delay: 0.2s;
        }
        .error {
          flex: 0 0 auto;
          color: #e60012;
          font-size: 12px;
          animation: ${shake} 0.2s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
          animation-delay: 0.2s;
        }
        .ok,
        .cancel {
          width: 100%;
          margin: 12px 0 0;
        }
        .ok {
          animation-delay: 0.3s;
        }
        .cancel {
          animation-delay: 0.4s;
          margin-bottom: 4px;
        }
      `}
    >
      <h1 className="title">{t('translation:dialog_warning')}</h1>
      <p className="desc">
        {snapshot &&
          (snapshot.designType === 'pro' ? t('confirm_deletion_pro') : t('confirm_deletion'))}
      </p>

      {showError && <p className="error">{error}</p>}

      <div className="ok">
        <ConfirmButton onClick={handleConfirmClick} loading={isLoading}>
          {t('translation:dialog_button_ok')}
        </ConfirmButton>
      </div>
      <div className="cancel">
        <NormalButton onClick={handleCancelClick} disabled={isLoading}>
          {t('translation:dialog_button_cancel')}
        </NormalButton>
      </div>
    </div>
  );
};

// helper function
function getText(t: TFunction, errorType?: MyDesignDeleteErrorType) {
  // doesn't handling 'no-exist' because page transitions.
  switch (errorType) {
    case 'unstable-network':
      return t('translation:error_unstable_network');
    case 'unknown':
    default:
      return t('translation:error_unknown');
  }
}
